import { LifecyclePanel } from './lifecycle-panel.js';
import { LeadReferralPanel } from './referrals-panel.js';
import { SendTimePanel } from './send-time-panel.js';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LeadPredictionPanel } from './lead-prediction-panel.js';
import { WebsiteActivityPanel } from './website-activity-panel.js';
import { useNavigate, useParams } from 'react-router-dom';
import { api, type ApiError, type GetLeadResult } from '@whinerp/client';
import {
  Button,
  EmptyState,
  ErrorState,
  Icon,
  Num,
  Panel,
  Skeleton,
  StageBar,
  useToast,
} from '@whinerp/ui';
import { formatCount, formatDateTime } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';
import { ConsentEvidencePanel } from './consent-evidence-panel.js';
import { RecordPage, TimelineSkeleton } from './record-page.tsx';
import { LocationChip } from './location-chip.js';
import { SourcePanel } from './source-panel.js';

/**
 * SC-10 Lead record.
 *
 * The status bar and the score breakdown sit above the record-page pattern, as
 * the spec asks. The score arrives with what it is made of, because a number a
 * rep cannot argue with is a number they will ignore - and one they ignore is
 * one that may as well not be computed.
 */

function LeadRecordScreen(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const params = useParams();
  const leadId = params['id'] ?? '';

  const lead = useQuery<GetLeadResult, ApiError>({
    queryKey: ['lead', leadId],
    queryFn: () => api.getLead({ path: { leadId } }),
  });

  const undo = useMutation({
    mutationFn: () => api.reverseLeadConversion({ path: { leadId }, body: {} }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['lead', leadId] });
      void queryClient.invalidateQueries({ queryKey: ['leads'] });
      toast.show({
        tone: 'info',
        title: t('lead.undo'),
        // Names what came back out. "Undone" alone leaves a person wondering
        // whether the company they had already edited is still there.
        body: result.removed.join(', '),
      });
    },
    onError: (error: ApiError) =>
      toast.show({ tone: 'error', title: t('state.error'), body: error.message }),
  });

  if (lead.isPending) return <LeadRecordLoading />;
  if (lead.isError) return <LeadRecordError error={lead.error} retry={() => void lead.refetch()} />;

  const detail = lead.data;

  return (
    <RecordPage
      objectKey="lead"
      recordId={leadId}
      title={detail.lead.fullName}
      subtitle={detail.lead.companyName ?? undefined}
      headerFieldKeys={['owner_id', 'source', 'updated_at']}
      related={
        <>
          <SourcePanel objectKey="lead" recordId={leadId} />
        <LeadPredictionPanel leadId={leadId} ruleScore={detail.lead.score} />
        <WebsiteActivityPanel objectKey="lead" recordId={leadId} />
        <LifecyclePanel objectKey="lead" recordId={leadId} />
        <SendTimePanel objectKey="lead" recordId={leadId} />
        <LeadReferralPanel leadId={leadId} />
          <ConsentEvidencePanel objectKey="lead" recordId={leadId} />
        </>
      }
      banner={
        <div className="flex flex-col gap-3">
          {/* Where it came from, said as an estimate (F-152). Drawn above the
              status because a rep reads it while the call is connecting. */}
          <LocationChip location={detail.location} />
          {/* The status bar. The workspace's own word for the status, never
              the stored key. */}
          <Panel data-region="status">
            <div className="flex flex-col gap-3">
              {/* Every status, not only the current one. A bar showing where a
                  lead is and not where it is going is a label, not a process. */}
              <StageBar
                stages={detail.statuses.map((status) => ({
                  key: status.key,
                  label: status.label,
                  ...(status.isWon ? { outcome: 'won' as const } : {}),
                  ...(status.isLost ? { outcome: 'lost' as const } : {}),
                }))}
                currentKey={detail.lead.status}
                labels={{
                  current: t('lead.status'),
                  done: t('state.saved'),
                  progress: t('lead.status'),
                }}
              />

            <div className="flex flex-wrap items-center gap-3">
              <span className="flex items-center gap-2">
                <span className="text-micro uppercase tracking-label text-ink-label">
                  {t('lead.score')}
                </span>
                <Num className="font-serif text-h2 text-ink">{formatCount(detail.lead.score, format)}</Num>
              </span>

              {detail.conversion.converted ? null : (
                <Button
                  intent="primary"
                  icon="ArrowsLeftRight"
                  data-convert
                  onClick={() => navigate(`/leads/${leadId}/convert`)}
                >
                  {t('lead.convert')}
                </Button>
              )}
            </div>
            </div>
          </Panel>

          {/* What the score is made of. */}
          {detail.scoreFactors.length > 0 ? (
            <Panel data-region="score">
              <div className="flex flex-col gap-1">
                <span className="text-micro uppercase tracking-label text-ink-label">
                  {t('lead.scoreBreakdown')}
                </span>
                <ul className="flex flex-col">
                  {detail.scoreFactors.map((factor) => (
                    <li
                      key={factor.label}
                      data-score-factor
                      className="flex items-center justify-between gap-3 border-t border-t-rule py-1 first:border-t-0"
                    >
                      <span className="text-dense text-ink">{factor.label}</span>
                      <Num align="end" className="text-dense text-ink-meta">
                        {formatCount(factor.points, format)}
                      </Num>
                    </li>
                  ))}
                </ul>
              </div>
            </Panel>
          ) : null}

          {/* A converted lead is not a dead end: it says where it went and,
              while the window is open, offers to undo. */}
          {detail.conversion.converted ? (
            <Panel data-region="converted">
              <div className="flex flex-wrap items-center gap-3">
                <Icon name="CheckCircle" size="inline" decorative />
                <span className="flex flex-col">
                  <span className="text-body text-ink">{t('lead.converted')}</span>
                  <span className="text-meta text-ink-meta">{t('lead.converted.body')}</span>
                </span>
                <span className="flex flex-wrap items-center gap-2">
                  {detail.conversion.accountId ? (
                    <a
                      href={`/accounts/${detail.conversion.accountId}`}
                      className="text-dense text-bronze-text underline underline-offset-4"
                    >
                      {t('convert.company')}
                    </a>
                  ) : null}
                  {detail.conversion.contactId ? (
                    <a
                      href={`/contacts/${detail.conversion.contactId}`}
                      className="text-dense text-bronze-text underline underline-offset-4"
                    >
                      {t('convert.person')}
                    </a>
                  ) : null}
                  {detail.conversion.dealId ? (
                    <a
                      href={`/deals/${detail.conversion.dealId}`}
                      className="text-dense text-bronze-text underline underline-offset-4"
                    >
                      {t('convert.deal')}
                    </a>
                  ) : null}
                </span>
                {detail.conversion.reversibleUntil ? (
                  <span className="ms-auto flex items-center gap-2">
                    <Num className="text-meta text-ink-meta">
                      {t('lead.undo.until', {
                        values: {
                          when: formatDateTime(detail.conversion.reversibleUntil, format),
                        },
                      })}
                    </Num>
                    <Button
                      intent="secondary"
                      data-undo-convert
                      disabled={undo.isPending}
                      onClick={() => undo.mutate()}
                    >
                      {t('lead.undo')}
                    </Button>
                  </span>
                ) : (
                  // The window has passed. Said in words rather than by the
                  // button quietly not being there.
                  <span data-undo-expired className="ms-auto text-meta text-ink-meta">
                    {t('lead.undo.expired')}
                  </span>
                )}
              </div>
            </Panel>
          ) : null}
        </div>
      }
    />
  );
}

function LeadRecordEmpty(): JSX.Element {
  const t = useT();
  return (
    <EmptyState
      reason="no-records"
      illustration="UserMinus"
      title={t('record.notFound')}
      body={t('record.notFound.body')}
      primary={{ label: t('object.lead.plural'), href: '/leads', icon: 'UserPlus' }}
    />
  );
}

function LeadRecordLoading(): JSX.Element {
  const t = useT();
  return (
    <div className="flex w-full flex-col gap-5" role="status" aria-live="polite" aria-busy>
      <span className="sr-only">{t('state.loading')}</span>
      <Skeleton className="h-line-h1 w-block-xl" />
      <Skeleton className="h-block-xs w-full" />
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-record">
        <TimelineSkeleton />
        <Skeleton className="h-block-xl w-full" />
      </div>
    </div>
  );
}

function LeadRecordError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="This lead could not be loaded"
      body={error instanceof Error ? error.message : 'The record did not load.'}
      onRetry={retry}
    />
  );
}

export const screen = defineScreen({
  id: 'SC-10',
  route: '/leads/:id',
  surface: 'page',
  title: 'Lead record',
  // F-026 rather than F-037: this is the lead record. Business-card parsing
  // is not built, and claiming it here hid that.
  features: ['F-026', 'F-039', 'F-019', 'F-152', 'F-079', 'F-082'],
  entitlement: 'F-036',
  reachedBy: [{ kind: 'screen', from: 'SC-09', how: 'the name in the leads list' }],
  components: ['RecordHeader', 'Timeline', 'DetailsPanel', 'StageBar'],
  elements: ['RecordPage'],
  sampleRecord: { objectKey: 'lead', param: 'id' },
  states: {
    empty: LeadRecordEmpty,
    loading: LeadRecordLoading,
    error: LeadRecordError,
  },
  component: LeadRecordScreen,
});

export default screen;
